import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormFieldAndLabelFactory } from '../../../components/Form/FormFieldAndLabelFactory';
import { FormFieldsContainer } from '../../../components/Form/FormComponents';
import { StickyFormButtons } from '../../../components/Form/StickyFormButtons';
import { generalLedgerFormSchema, GeneralLedgerPayload } from '../../../schemas/GeneralLedgerPayload.schema';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { schemaToFormFields } from '../../../util/schema-utils';
import { IFormField } from '../../../view-models/form.view-model';
import { MultiFileUploadForm } from './MultiFileUploadForm';

interface IUploadLedgerFormProps {
  onClose: () => void;
}

export function UploadLedgerForm({ onClose }: IUploadLedgerFormProps) {
  const { useKpiFileUpload3 } = useFlags<MaggieFeatureFlags>();
  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
  const schema = useMemo(() => generalLedgerFormSchema(useKpiFileUpload3), [useKpiFileUpload3]);
  const methods = useForm<GeneralLedgerPayload>({
    resolver: yupResolver(schema),
  });

  const fieldsMap = useMemo(() => {
    return schemaToFormFields(schema).reduce(
      (map, field) => map.set(field.key, field),
      new Map<string, IFormField<unknown>>()
    );
  }, [schema]);

  const handleSubmit = useCallback(() => {
    const { file } = methods.getValues();
    const files: File[] = Array.isArray(file) ? file : [file as File];
    setSelectedFiles(files);
  }, [methods]);

  // Step 2: Preview + upload with per-file status (MultiFileUploadForm parses
  // filenames once via filesToEntries — no double-parse here).
  if (selectedFiles) {
    return (
      <MultiFileUploadForm
        initialFiles={selectedFiles}
        onClose={onClose}
        onBack={() => setSelectedFiles(null)}
      />
    );
  }

  // Step 1: File selection (SOI-style picker)
  return (
    <FormFieldsContainer css={fullWidth}>
      <FormProvider {...methods}>
        <FormFieldAndLabelFactory formField={fieldsMap.get('file')!} />
        <StickyFormButtons
          onCancel={onClose}
          onSubmit={methods.handleSubmit(handleSubmit)}
          loading={false}
          style={{ padding: '0.5rem 0 1rem' }}
          submitLabel='Continue'
        />
      </FormProvider>
    </FormFieldsContainer>
  );
}

// Override FormFieldsContainer's fixed 43.5rem width so the dropzone fills the
// dialog content area (avoids a narrow column with a lot of whitespace
// shoulders inside the Material UI 'md' dialog). max-width still caps via
// the parent style so very-wide screens stay readable.
const fullWidth = css`
  width: 100%;
  padding-top: 0.5rem;
  padding-bottom: 0;
`;
